import React, { ChangeEvent, useRef } from 'react';
import { Form } from 'react-bootstrap';
import {
  Control,
  Controller,
  FieldValues,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
import { FaPercent } from 'react-icons/fa';
import { InputContainer } from '../styles';
import { pesoFormatado } from './formatMask';
import { IconContainer, InputIcon } from './styles';

interface InputPercentProps extends React.HTMLProps<HTMLInputElement> {
  label?: string;
  placeholder?: string;
  name: string;
  register: UseFormRegister<FieldValues>;
  isError: boolean;
  maxLength?: number;
  max?: number;
  min?: number;
  decimals?: number;
  step?: string;
  showIcon?: boolean;
  isDisabled?: boolean;
  qtdDecimais?: number;
  control?: Control<FieldValues, any>;
  setValue?: UseFormSetValue<FieldValues>;
}

const PERCENT_INPUT_REGEX = /^[0-9,.]+$/;

const isClearableValue = (value: string) => value === '' || value === '0,0';

const parseFormattedPercentValue = (value: string) => {
  return Number.parseFloat(value.replace(/\./g, '').replace(',', '.'));
};

interface FormatValueOptions {
  decimals: number;
  max: number;
  min: number;
  previousValue: string;
  fallbackValue?: string;
  validateMin: boolean;
}

const formatInputValue = (
  value: string,
  {
    decimals,
    max,
    min,
    previousValue,
    fallbackValue = previousValue,
    validateMin,
  }: FormatValueOptions,
) => {
  if (isClearableValue(value)) {
    return {
      nextPreviousValue: '',
      value: '',
    };
  }

  if (!PERCENT_INPUT_REGEX.test(value)) {
    return {
      nextPreviousValue: previousValue,
      value: fallbackValue,
    };
  }

  const serializedValue = pesoFormatado(value, decimals);
  const parsedValue = parseFormattedPercentValue(serializedValue);

  if (Number.isNaN(parsedValue)) {
    return {
      nextPreviousValue: previousValue,
      value: fallbackValue,
    };
  }

  if (validateMin && parsedValue <= min && parsedValue !== 0) {
    return {
      nextPreviousValue: previousValue,
      value: fallbackValue,
    };
  }

  if (parsedValue > max) {
    return {
      nextPreviousValue: previousValue,
      value: fallbackValue,
    };
  }

  return {
    nextPreviousValue: serializedValue,
    value: serializedValue,
  };
};

export const InputPercent: React.FC<InputPercentProps> = ({
  label = '',
  placeholder = '',
  max = 100,
  min = 0,
  name,
  register,
  isError,
  showIcon = true,
  step = '1',
  isDisabled = false,
  maxLength = 6,
  qtdDecimais = 2,
  decimals,
  control,
  setValue,
  ...rest
}) => {
  const previousValueRef = useRef('');
  const registeredField = register(name);
  const decimalPlaces = decimals ?? qtdDecimais;

  const applyFormattedValue = (
    event: ChangeEvent<HTMLInputElement>,
    {
      fallbackValue,
      validateMin,
    }: {
      fallbackValue?: string;
      validateMin: boolean;
    },
  ) => {
    const formattedResult = formatInputValue(event.target.value, {
      decimals: decimalPlaces,
      max,
      min,
      previousValue: previousValueRef.current,
      fallbackValue,
      validateMin,
    });

    previousValueRef.current = formattedResult.nextPreviousValue;
    event.target.value = formattedResult.value;

    return formattedResult.value;
  };

  const onChangeValue = (event: ChangeEvent<HTMLInputElement>) => {
    applyFormattedValue(event, { validateMin: true });
    registeredField.onChange(event);

    if (setValue) {
      setValue(name, event.target.value);
    }
  };

  const getFormattedValue = (
    event: ChangeEvent<HTMLInputElement>,
    fallbackValue?: string,
  ) => {
    return applyFormattedValue(event, {
      fallbackValue,
      validateMin: false,
    });
  };

  return (
    <InputContainer>
      <Form.Group>
        {label && <Form.Label>{label}</Form.Label>}

        <InputIcon showIcon={showIcon}>
          <div style={{ position: 'relative' }}>
            {control ? (
              <Controller
                name={name}
                control={control}
                render={({ field: { value, onChange, onBlur, ref } }) => {
                  const currentValue =
                    value === null || value === undefined ? '' : String(value);

                  previousValueRef.current = currentValue;

                  return (
                    <input
                      ref={ref}
                      name={name}
                      type="text"
                      value={currentValue}
                      maxLength={maxLength}
                      step={step}
                      className={
                        isError ? 'form-control is-invalid' : 'form-control'
                      }
                      placeholder={placeholder}
                      onBlur={onBlur}
                      onChange={(event: ChangeEvent<HTMLInputElement>) => {
                        const formattedValue = getFormattedValue(
                          event,
                          currentValue,
                        );
                        onChange(formattedValue);
                      }}
                      disabled={isDisabled}
                      autoComplete="off"
                      {...rest}
                    />
                  );
                }}
              />
            ) : (
              <input
                {...registeredField}
                type="text"
                maxLength={maxLength}
                step={step}
                className={isError ? 'form-control is-invalid' : 'form-control'}
                placeholder={placeholder}
                onChange={onChangeValue}
                autoComplete="off"
                disabled={isDisabled}
                {...rest}
              />
            )}

            {showIcon && (
              <IconContainer className="container-icon">
                <FaPercent className="input-icon" />
              </IconContainer>
            )}
          </div>
        </InputIcon>
      </Form.Group>
    </InputContainer>
  );
};
